import { useIntl } from 'react-intl';
import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useListMoneyAccounts } from 'src/features/moneyAccounts/api/useListMoneyAccounts';
import { createColumnHelper } from '@tanstack/react-table';
import { MoneyAccount } from 'src/features/moneyAccounts/utils/types';
import { commonMessages } from 'src/utils/messages';
import { moneyAccountsMessages } from 'src/features/moneyAccounts/utils/messages';
import CreateMoneyAccountFormModal from 'src/features/moneyAccounts/components/forms/CreateMoneyAccountFormModal';
import CellMoneyAccountActions from 'src/features/moneyAccounts/components/tables/CellMoneyAccountActions';
import { MONEY_ACCOUNT_TYPE_BADGE_MAP } from 'src/features/moneyAccounts/utils/constants';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import useSearch from 'src/registry/hooks/useSearch';
import { booleanBadgeOptions } from 'src/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import { useParams } from 'react-router-dom';
import usePagination from 'src/registry/hooks/usePagination';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { billingMessages } from 'src/features/billing/utils/messages';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const MoneyAccountsTable = () => {
    const { formatMessage } = useIntl();
    const { entityId = '' } = useParams();

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();

    const permissionManageMoneyAccounts = useHasPermissions([PERMISSION.MANAGE_MONEY_ACCOUNTS]);
    const permissionAccessBillingEntities = useHasPermissions([PERMISSION.ACCESS_BILLING_ENTITIES]);

    const {
        data: moneyAccounts,
        isLoading,
        isError,
    } = useListMoneyAccounts({
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...queryPaginationOptions,
        ...(entityId && { billing_entity: entityId }),
    });

    const columnHelper = createColumnHelper<MoneyAccount>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        ...(!entityId
            ? [
                  columnHelper.accessor('billing_entity.name', {
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      cell: (ctx) => {
                          const entityId = ctx.row.original.billing_entity?.id;
                          return (
                              <CellLinkWithIcon
                                  {...ctx}
                                  to={`/billing-entity/${entityId}`}
                                  hasPermissions={permissionAccessBillingEntities}
                              />
                          );
                      },
                      meta: {
                          title: formatMessage(billingMessages.billingEntity),
                      },
                  }),
              ]
            : []),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: MONEY_ACCOUNT_TYPE_BADGE_MAP,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('iban', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(moneyAccountsMessages.iban),
            },
        }),
        columnHelper.accessor('currency', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return <CellCurrencyBadge currency={row.original.currency} />;
            },
            meta: {
                title: formatMessage(commonMessages.currency),
            },
        }),
        columnHelper.accessor('is_active', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(moneyAccountsMessages.isActive),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        ...(permissionManageMoneyAccounts
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellMoneyAccountActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];
    return (
        <DataTable<MoneyAccount, unknown>
            columns={columns}
            data={moneyAccounts}
            semantics={{
                title: formatMessage(moneyAccountsMessages.moneyAccounts),
                entryName: formatMessage(moneyAccountsMessages.account).toLowerCase(),
                entryNames: formatMessage(moneyAccountsMessages.accounts).toLowerCase(),
                placeholder: formatMessage(moneyAccountsMessages.account).toLowerCase(),
            }}
            search={search}
            filters={filters}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageMoneyAccounts && { newEntry: CreateMoneyAccountFormModal }),
            }}
        />
    );
};

export default MoneyAccountsTable;
